<template>
  <div class="pa-4">
    <div class="d-flex align-center mb-4">
      <v-btn icon @click="callBackRoute" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-h6">Draft DAs</span>
      <v-icon small class="mx-1">mdi-chevron-right</v-icon>
      <span class="text-h6 primary--text mr-3">{{ draftDA.dda_no }}</span>
      <v-chip small label :color="statusColor" text-color="white" class="font-weight-bold mr-2">
        {{ statusLabel }}
      </v-chip>
      <v-chip small outlined>{{ eventTypeLabel }}</v-chip>
      <v-spacer />
      <template v-if="draftDA.status === 'open'">
        <v-btn color="error" outlined small class="mr-2" @click="showCancelDialog = true">
          <v-icon x-small class="mr-1">mdi-close-circle-outline</v-icon>
          Cancel
        </v-btn>
        <v-btn color="primary" small depressed @click="showConvertDialog = true">
          <v-icon x-small class="mr-1">mdi-file-check-outline</v-icon>
          Convert to DA
        </v-btn>
      </template>
    </div>

    <div v-if="draftDA.failure_reason" class="failure-banner mb-3">
      <div class="failure-banner__title">
        <v-icon color="#b45309" size="16">mdi-alert-circle-outline</v-icon>
        Why this is a Draft DA
      </div>
      <ul class="failure-banner__list">
        <li v-for="(reason, i) in failureReasonList" :key="i">{{ reason }}</li>
      </ul>
    </div>

    <v-card flat outlined class="rounded mb-3">
      <div class="section-head">Movement Details</div>

      <div class="route-strip">
        <div class="route-strip__lane">
          <div class="field-label">Lane</div>
          <div class="field-value">{{ draftDA.lane_title || "-" }}</div>
        </div>
        <div class="route-strip__path">
          <div class="field-label">Origin</div>
          <div v-if="draftDA.origin_name" class="field-value field-value--accent">{{ draftDA.origin_name }}</div>
          <div v-else-if="draftDA.origin_raw" class="field-value field-value--unresolved" :title="`'${draftDA.origin_raw}' not found in the system`">
            {{ draftDA.origin_raw }}
          </div>
          <div v-else class="field-value">-</div>
        </div>
        <v-icon size="16" color="#9e9e9e">mdi-arrow-right</v-icon>
        <div class="route-strip__path">
          <div class="field-label">Destination</div>
          <div v-if="draftDA.destination_name" class="field-value field-value--accent">{{ draftDA.destination_name }}</div>
          <div v-else-if="draftDA.destination_raw" class="field-value field-value--unresolved" :title="`'${draftDA.destination_raw}' not found in the system`">
            {{ draftDA.destination_raw }}
          </div>
          <div v-else class="field-value">-</div>
        </div>
      </div>

      <div class="field-grid">
        <div class="field-cell">
          <div class="field-label">Container No</div>
          <div class="field-value">{{ draftDA.container_number || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Container Type</div>
          <div v-if="draftDA.container_type_name" class="field-value">{{ draftDA.container_type_name }}</div>
          <div v-else-if="draftDA.container_type_raw" class="field-value field-value--unresolved" :title="`'${draftDA.container_type_raw}' not found in the system`">
            {{ draftDA.container_type_raw }}
          </div>
          <div v-else class="field-value">-</div>
        </div>
        <div class="field-cell">
          <div class="field-label">VBS Reference</div>
          <div class="field-value">{{ draftDA.vbs_reference_no || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Voyage ID</div>
          <div class="field-value">{{ draftDA.voyage_id || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Job Type</div>
          <div class="field-value">{{ draftDA.job_type || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Sub Hire</div>
          <div class="field-value">{{ draftDA.is_subhire ? "Yes" : "No" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Vendor</div>
          <div v-if="draftDA.vendor_name" class="field-value">{{ draftDA.vendor_name }}</div>
          <div v-else-if="draftDA.vendor_name_raw" class="field-value field-value--unresolved" :title="`'${draftDA.vendor_name_raw}' not found in the system`">
            {{ draftDA.vendor_name_raw }}
          </div>
          <div v-else class="field-value">-</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Customer</div>
          <div class="field-value">{{ draftDA.customer_name || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">DA Rate</div>
          <div class="field-value">{{ draftDA.rate ? `AED ${draftDA.rate}` : "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Sub Hire Rate</div>
          <div class="field-value">{{ draftDA.subhire_rate ? `AED ${draftDA.subhire_rate}` : "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Vehicle No (CODECO)</div>
          <div class="field-value field-value--accent">{{ draftDA.vehicle_no || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">System Vehicle</div>
          <div class="field-value field-value--accent">{{ draftDA.vehicle_name || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Driver</div>
          <div class="field-value">{{ draftDA.driver_name || draftDA.driver_id_raw || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Gate Out Terminal</div>
          <div class="field-value">{{ draftDA.gate_out_terminal || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Gate In Terminal</div>
          <div class="field-value">{{ draftDA.gate_in_terminal || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Movement Date</div>
          <div class="field-value">{{ formatDate(draftDA.movement_date) || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Gate Out Timestamp</div>
          <div class="field-value">{{ formatDate(draftDA.pickup_departure, true) || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Gate In Timestamp</div>
          <div class="field-value">{{ formatDate(draftDA.drop_arrival, true) || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">CODECO Gate Out Driver</div>
          <div class="field-value">{{ draftDA.codeco_gate_out_driver || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">CODECO Gate Out Vehicle</div>
          <div class="field-value">{{ draftDA.codeco_gate_out_vehicle || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">CODECO Gate In Driver</div>
          <div class="field-value">{{ draftDA.codeco_gate_in_driver || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">CODECO Gate In Vehicle</div>
          <div class="field-value">{{ draftDA.codeco_gate_in_vehicle || "-" }}</div>
        </div>
      </div>
    </v-card>

    <v-card flat outlined class="rounded mb-3">
      <div class="section-head">Conversion</div>
      <div class="field-grid field-grid--four">
        <div class="field-cell">
          <div class="field-label">Emergency Order Container</div>
          <router-link
            v-if="draftDA.emergency_order_container_id"
            :to="`/app/admin/emergency-order-container/details/${draftDA.emergency_order_container_id}`"
            class="field-value link-cell"
          >
            View Container
            <v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
          </router-link>
          <div v-else class="field-value">-</div>
          <v-chip
            v-if="draftDA.emergency_order_container_status"
            x-small
            label
            :color="statusChipColor(draftDA.emergency_order_container_status)"
            text-color="white"
            class="font-weight-bold mt-1"
          >
            {{ draftDA.emergency_order_container_status }}
          </v-chip>
        </div>
        <div class="field-cell">
          <div class="field-label">Delivery Advice</div>
          <router-link
            v-if="draftDA.da"
            :to="`/app/admin/delivery-advice/details/${draftDA.da}`"
            class="field-value link-cell"
          >
            {{ draftDA.da_no }}
            <v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
          </router-link>
          <div v-else class="field-value">-</div>
          <v-chip
            v-if="draftDA.da_status"
            x-small
            label
            :color="statusChipColor(draftDA.da_status)"
            text-color="white"
            class="font-weight-bold mt-1"
          >
            {{ draftDA.da_status }}
          </v-chip>
        </div>
        <div class="field-cell">
          <div class="field-label">Confirmed On</div>
          <div class="field-value">{{ formatDate(draftDA.confirmed_on, true) || "-" }}</div>
        </div>
        <div class="field-cell">
          <div class="field-label">Created</div>
          <div class="field-value">{{ formatDate(draftDA.created, true) || "-" }}</div>
        </div>
      </div>
    </v-card>

    <v-card flat outlined class="rounded">
      <div class="section-head">
        Activity Log
        <v-spacer />
        <span class="section-head__meta">{{ (draftDA.logs || []).length }} event(s)</span>
      </div>
      <div class="pa-3 activity-log">
        <div v-for="log in draftDA.logs" :key="log.id" class="activity-log__item">
          <div class="d-flex justify-space-between align-baseline">
            <span class="activity-log__author">{{ log.updated_by_name || "System" }}</span>
            <span class="activity-log__time">{{ formatDate(log.created, true) }}</span>
          </div>
          <div class="activity-log__message">{{ log.message }}</div>
        </div>
        <p v-if="!draftDA.logs || !draftDA.logs.length" class="caption grey--text text-center ma-0 py-2">
          No activity logs yet.
        </p>
      </div>
    </v-card>

    <CreateContainerDADialog
      v-if="!draftDA.is_subhire"
      v-model="showConvertDialog"
      :container="convertContainer"
      @daCreated="onConfirmed"
    />
    <CreateSubhireContainerDADialog
      v-else
      v-model="showConvertDialog"
      :container="convertContainer"
      @daCreated="onConfirmed"
    />

    <v-dialog max-width="440px" v-model="showCancelDialog" persistent>
      <v-card>
        <v-card-title class="secondary white--text py-3 px-5">
          <span class="text-subtitle-1 font-weight-bold">Cancel Draft DA</span>
          <v-spacer />
          <v-btn icon color="white" small @click="showCancelDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="px-5 pt-4">
          <v-textarea
            label="Remarks (optional)"
            outlined
            dense
            hide-details="auto"
            rows="3"
            v-model="cancelRemarks"
          />
        </v-card-text>
        <v-divider class="mt-3" />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn depressed outlined @click="showCancelDialog = false" class="mr-2">Back</v-btn>
          <v-btn color="error" depressed :loading="cancelling" min-width="120" @click="confirmCancel">
            <v-icon small class="mr-1">mdi-close-circle-outline</v-icon>
            Cancel Draft
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import CreateContainerDADialog from "@/components/ModuleBased/dialogForms/CreateContainerDADialog.vue";
import CreateSubhireContainerDADialog from "@/components/ModuleBased/dialogForms/CreateSubhireContainerDADialog.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
  formatDate,
} from "@/utils/functions";

export default {
  components: { CreateContainerDADialog, CreateSubhireContainerDADialog },
  data() {
    return {
      draftDA: {},
      permissions: null,
      showConvertDialog: false,
      showCancelDialog: false,
      cancelRemarks: "",
      cancelling: false,
    };
  },
  computed: {
    statusLabel() {
      const labels = { open: "Open", confirmed: "Confirmed", cancelled: "Cancelled" };
      return labels[this.draftDA.status] || this.draftDA.status;
    },
    statusColor() {
      const colors = {
        open: "amber darken-2",
        confirmed: "green darken-1",
        cancelled: "red darken-1",
      };
      return colors[this.draftDA.status] || "grey";
    },
    eventTypeLabel() {
      return this.draftDA.event_type === "gate_in" ? "Gate In" : "Gate Out";
    },
    failureReasonList() {
      return (this.draftDA.failure_reason || "")
        .split("\n")
        .map((reason) => reason.trim())
        .filter(Boolean);
    },
    // CreateContainerDADialog / CreateSubhireContainerDADialog operate on an
    // EmergencyOrderContainer (container.id drives createManualDA(id, ...)) -
    // the Draft DA's underlying container is what actually gets the DA, so
    // the dialog is pointed at that container's id and its already-known
    // fields are pre-filled from the draft's own snapshot.
    convertContainer() {
      return {
        id: this.draftDA.emergency_order_container_id,
        origin: this.draftDA.origin,
        destination: this.draftDA.destination,
        container_type: this.draftDA.container_type,
        job_type: this.draftDA.job_type,
        driver: this.draftDA.driver,
        vehicle: this.draftDA.vehicle,
        is_subhire: this.draftDA.is_subhire,
        vendor_name: this.draftDA.vendor_name,
      };
    },
  },
  methods: {
    formatDate,
    statusChipColor(status) {
      const colors = {
        Pending: "grey darken-1",
        Created: "grey darken-1",
        Scheduled: "blue darken-1",
        Assigned: "blue darken-1",
        Active: "orange darken-1",
        Completed: "green darken-1",
        "Ready For Invoice": "teal darken-1",
        "Invoice Generated": "purple darken-1",
        Cancelled: "red darken-1",
      };
      return colors[status] || "grey";
    },
    callBackRoute() {
      // Goes back in browser history instead of a hardcoded path, so it
      // returns to whichever Draft DA view (plain list, or a category like
      // ML Bonded/Subhire Bonded) the user actually came from, rather than
      // always dropping them on the unfiltered list.
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push("/app/admin/draft-da/list");
      }
    },
    getDraftDADetails() {
      this.$bus.$emit("showLoader", true);
      this.$api.draftDA
        .getDraftDAObject(this.$route.params.id)
        .then((res) => {
          this.draftDA = res.data;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    onConfirmed() {
      this.$bus.$emit("showToastMessage", {
        message: "Draft DA confirmed successfully!",
        color: "success",
      });
      this.getDraftDADetails();
    },
    confirmCancel() {
      this.cancelling = true;
      this.$api.draftDA
        .cancelDraftDA(this.draftDA.id, { remarks: this.cancelRemarks })
        .then(() => {
          this.$bus.$emit("showToastMessage", {
            message: "Draft DA cancelled.",
            color: "success",
          });
          this.showCancelDialog = false;
          this.cancelRemarks = "";
          this.getDraftDADetails();
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: err?.message || "Failed to cancel Draft DA.",
            color: "error",
          });
        })
        .finally(() => {
          this.cancelling = false;
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("emergency_order_container");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this module!",
          color: "error",
        });
        this.$router.push(
          `/${localStorage.getItem("user_type").toLowerCase()}`,
        );
      } else {
        this.permissions = { ...permissions };
        this.getDraftDADetails();
      }
    }
  },
};
</script>
<style scoped>
.rounded {
  border-radius: 8px !important;
}
.section-head {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.6);
  background-color: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.section-head__meta {
  font-size: 11px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  color: rgba(0, 0, 0, 0.45);
}

.failure-banner {
  border: 1px solid #fbbf24;
  background: #fffbeb;
  border-radius: 8px;
  padding: 10px 16px;
}
.failure-banner__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 6px;
}
.failure-banner__list {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  color: #78350f;
}
.failure-banner__list li + li {
  margin-top: 2px;
}

.route-strip {
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 10px 16px;
  background-color: #fcfcfc;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.route-strip__path {
  display: flex;
  flex-direction: column;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0;
}
.field-grid--four {
  grid-template-columns: repeat(4, 1fr);
}
.field-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  min-width: 0;
}
.field-cell .field-value {
  margin-bottom: 2px;
}
.field-cell .v-chip {
  margin-top: auto;
}
.field-cell:nth-child(5n) {
  border-right: none;
}
.field-grid--four .field-cell:nth-child(4n) {
  border-right: none;
}
.field-cell:nth-last-child(-n + 5) {
  border-bottom: none;
}

.field-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 2px;
}
.field-value {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.field-value--accent {
  color: var(--v-primary-base, #1976d2);
}
.field-value--unresolved {
  color: #b45309;
  cursor: help;
}
.field-value--unresolved::after {
  content: " (unresolved)";
  font-size: 10px;
  font-weight: 500;
  color: #b45309;
  opacity: 0.75;
}
.link-cell {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.activity-log {
  max-height: 360px;
  overflow-y: auto;
}
.activity-log__item {
  padding: 8px 10px;
  border-radius: 6px;
}
.activity-log__item + .activity-log__item {
  margin-top: 4px;
}
.activity-log__item:nth-child(odd) {
  background: #fafafa;
}
.activity-log__author {
  font-size: 11px;
  font-weight: 700;
  color: #e65100;
}
.activity-log__time {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
}
.activity-log__message {
  font-size: 13px;
  margin-top: 2px;
  line-height: 1.4;
}

@media (max-width: 960px) {
  .field-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .field-grid--four {
    grid-template-columns: repeat(2, 1fr);
  }
  .field-cell:nth-child(5n),
  .field-grid--four .field-cell:nth-child(4n) {
    border-right: 1px solid rgba(0, 0, 0, 0.06);
  }
  .field-cell:nth-child(2n) {
    border-right: none;
  }
  .field-cell:nth-last-child(-n + 5) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
  .field-cell:nth-last-child(-n + 2) {
    border-bottom: none;
  }
}
</style>
